import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import {
  apdmCouponItemUsable,
  couponBenefitText,
} from '@euler/functions/apdm/couponRedemption';
import {
  ApdmRedemptionPhase,
  apdmCouponPickLimit,
  useApdmCouponRedemption,
} from '@euler/functions/apdm/useApdmCouponRedemption';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ApdmPlateCouponItem, ApdmPlateCouponState } from '@euler/model/apdm';
import dayjs from 'dayjs';
import {
  Check,
  CircleCheck,
  Minus,
  Plus,
  RefreshCw,
  RotateCw,
  ScanLine,
  ScanQrCode,
  Ticket,
  TriangleAlert,
} from 'lucide-react-native';
import { ReactNode, memo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type Workspace = ReturnType<typeof useApdmCouponRedemption>;

const kCouponStateLabels: Record<ApdmPlateCouponState, string> = {
  [ApdmPlateCouponState.Usable]: '有效',
  [ApdmPlateCouponState.UsedUp]: '已用完',
  [ApdmPlateCouponState.Expired]: '已过期',
};

const kColumn = { width: '100%', maxWidth: 560, alignSelf: 'center' } as const;

export function ApdmCouponRedemptionWorkspace({
  workspace,
  onScan,
}: {
  workspace: Workspace;
  /** Opens the camera; absent where there is none (web). */
  onScan?: () => void;
}) {
  const { rpx } = useResponsiveContext();
  const insets = useSafeAreaInsets();
  const { state } = workspace;
  // A fresh read clears the coupon; only re-reading one keeps it shown.
  const showCoupon =
    state.coupon != null && state.phase !== ApdmRedemptionPhase.Scan;
  return (
    <View style={{ flex: 1, backgroundColor: aiColors.background }}>
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          padding: rpx(16),
          paddingBottom: rpx(24),
        }}
      >
        <View style={[kColumn, { gap: rpx(16) }]}>
          {showCoupon ? (
            <CouponView workspace={workspace} />
          ) : (
            <ScanCard workspace={workspace} onScan={onScan} />
          )}
        </View>
      </ScrollView>
      {showCoupon ? (
        <View
          style={{
            paddingHorizontal: rpx(16),
            paddingTop: rpx(12),
            paddingBottom: insets.bottom + rpx(12),
            backgroundColor: aiColors.card,
            borderTopWidth: 1,
            borderTopColor: aiColors.border,
          }}
        >
          <View style={kColumn}>
            <BottomBar workspace={workspace} onScan={onScan} />
          </View>
        </View>
      ) : null}
    </View>
  );
}

const ScanCard = memo(
  ({ workspace, onScan }: { workspace: Workspace; onScan?: () => void }) => {
    const { rpx } = useResponsiveContext();
    const { state, actions } = workspace;
    const [manual, setManual] = useState(!onScan);
    const [raw, setRaw] = useState('');
    const reading = state.phase === ApdmRedemptionPhase.Reading;
    return (
      <View
        style={{
          alignItems: 'center',
          gap: rpx(12),
          padding: rpx(24),
          borderRadius: rpx(16),
          backgroundColor: aiColors.card,
          borderWidth: 1,
          borderColor: aiColors.border,
        }}
      >
        <View
          style={{
            width: rpx(64),
            height: rpx(64),
            borderRadius: rpx(18),
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: aiColors.secondary,
          }}
        >
          {reading ? (
            <ActivityIndicator color={aiColors.primary} />
          ) : (
            <ScanLine size={32} color={aiColors.primary} />
          )}
        </View>
        <Typography.Text medium size={rpx(16)} color={aiColors.foreground}>
          {reading ? '正在读取优惠券…' : '扫描客户的优惠券二维码'}
        </Typography.Text>
        <Typography.Text
          size={rpx(12)}
          color={aiColors.mutedForeground}
          style={{ textAlign: 'center' }}
        >
          {onScan
            ? '对准客户手机上的优惠券海报二维码'
            : '用扫码枪扫描，或粘贴二维码内容'}
        </Typography.Text>
        {state.error ? <Notice tone="error" text={state.error} /> : null}
        {onScan ? (
          <PrimaryButton
            icon={<ScanQrCode size={18} color="#fff" />}
            title="扫一扫"
            disabled={reading}
            onPress={onScan}
          />
        ) : null}
        {manual ? (
          <View style={{ alignSelf: 'stretch', gap: rpx(8) }}>
            <TextInput
              value={raw}
              onChangeText={setRaw}
              editable={!reading}
              placeholder="粘贴二维码内容"
              placeholderTextColor={aiColors.mutedForeground}
              autoCapitalize="none"
              autoCorrect={false}
              onSubmitEditing={() => void actions.load(raw)}
              style={{
                paddingHorizontal: rpx(12),
                paddingVertical: rpx(10),
                borderRadius: rpx(10),
                borderWidth: 1,
                borderColor: aiColors.border,
                backgroundColor: aiColors.input,
                color: aiColors.foreground,
                fontSize: rpx(14),
              }}
            />
            <PrimaryButton
              title="读取优惠券"
              secondary={!!onScan}
              disabled={reading || !raw.trim()}
              onPress={() => void actions.load(raw)}
            />
          </View>
        ) : (
          <Pressable
            accessibilityRole="button"
            onPress={() => setManual(true)}
            hitSlop={8}
          >
            <Typography.Text size={rpx(13)} color={aiColors.primary}>
              手动输入
            </Typography.Text>
          </Pressable>
        )}
      </View>
    );
  },
);

const CouponView = memo(({ workspace }: { workspace: Workspace }) => {
  const { rpx } = useResponsiveContext();
  const { state, actions } = workspace;
  const coupon = state.coupon!;
  const done = state.phase === ApdmRedemptionPhase.Done ? state.done : null;
  const editable = state.phase === ApdmRedemptionPhase.Ready;
  const usable = coupon.state === ApdmPlateCouponState.Usable;
  const doneCount = done
    ? done.lines.reduce((sum, line) => sum + line.couponIds.length, 0)
    : 0;
  const note =
    state.phase === ApdmRedemptionPhase.Redeeming
      ? '核销中，请稍候'
      : state.phase === ApdmRedemptionPhase.Unsure
      ? '核销已锁定'
      : done
      ? `本次已核销 ${doneCount} 份`
      : '可多选';
  return (
    <>
      <View
        style={{
          padding: rpx(16),
          gap: rpx(12),
          borderRadius: rpx(16),
          backgroundColor: aiColors.card,
          borderWidth: 1,
          borderColor: aiColors.border,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
          <View style={{ flex: 1, gap: rpx(2) }}>
            <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
              车牌号
            </Typography.Text>
            <Typography.Text bold size={rpx(24)} color={aiColors.foreground}>
              {coupon.licensePlateNo}
            </Typography.Text>
          </View>
          <Pill
            text={kCouponStateLabels[coupon.state]}
            color={usable ? aiColors.green : aiColors.mutedForeground}
          />
        </View>
        <View style={{ height: 1, backgroundColor: aiColors.border }} />
        <View style={{ flexDirection: 'row' }}>
          <Fact label="客户" value={coupon.customerName || '—'} />
          <Fact
            label="有效期至"
            value={
              !usable
                ? '—'
                : coupon.expiresAt
                ? dayjs(coupon.expiresAt).format('YYYY年M月D日')
                : '长期有效'
            }
          />
        </View>
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(6) }}
        >
          <Ticket size={14} color={aiColors.mutedForeground} />
          <Typography.Text
            size={rpx(12)}
            color={aiColors.mutedForeground}
            numberOfLines={1}
            style={{ flex: 1 }}
          >
            {coupon.campaignTitle}
          </Typography.Text>
        </View>
      </View>
      <View style={{ gap: rpx(10) }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography.Text medium size={rpx(14)} color={aiColors.foreground}>
            优惠项目
          </Typography.Text>
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {note}
          </Typography.Text>
        </View>
        {coupon.items.map(item => (
          <ItemRow
            key={item.itemId}
            item={item}
            picked={state.picks[item.itemId] ?? 0}
            limit={apdmCouponPickLimit(
              item.claimableCouponIds.length,
              state.picks,
              item.itemId,
            )}
            redeemed={
              done?.lines.find(line => line.itemId === item.itemId)?.couponIds
                .length ?? 0
            }
            redeemedAt={done?.redeemedAt ?? null}
            editable={editable}
            onToggle={() => actions.toggle(item.itemId)}
            onCount={count => actions.setCount(item.itemId, count)}
          />
        ))}
      </View>
    </>
  );
});

const ItemRow = memo(
  ({
    item,
    picked,
    limit,
    redeemed,
    redeemedAt,
    editable,
    onToggle,
    onCount,
  }: {
    item: ApdmPlateCouponItem;
    picked: number;
    /** The most that can be picked of it in this batch. */
    limit: number;
    /** How many this item had redeemed in the batch just done. */
    redeemed: number;
    redeemedAt: string | null;
    editable: boolean;
    onToggle: () => void;
    onCount: (count: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const usable = apdmCouponItemUsable(item);
    const left = item.claimableCouponIds.length;
    const unit = item.qtyUnit ?? '份';
    const checked = picked > 0;
    const success = redeemed > 0;
    const accent = success
      ? aiColors.green
      : checked
      ? aiColors.primary
      : aiColors.border;
    return (
      <Pressable
        accessibilityRole="checkbox"
        accessibilityState={{
          checked: checked || success,
          disabled: !editable || !usable,
        }}
        disabled={!editable || !usable}
        onPress={onToggle}
        style={{
          flexDirection: 'row',
          gap: rpx(12),
          padding: rpx(14),
          borderRadius: rpx(14),
          borderWidth: checked || success ? 1.5 : 1,
          borderColor: accent,
          backgroundColor: success
            ? tint(aiColors.green, 0.06)
            : checked
            ? tint(aiColors.primary, 0.06)
            : usable
            ? aiColors.card
            : aiColors.muted,
          opacity: usable || success ? 1 : 0.6,
        }}
      >
        <View
          style={{
            width: rpx(20),
            height: rpx(20),
            marginTop: rpx(1),
            borderRadius: rpx(5),
            borderWidth: checked || success ? 0 : 1.5,
            borderColor: aiColors.border,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: success
              ? aiColors.green
              : checked
              ? aiColors.primary
              : aiColors.card,
          }}
        >
          {checked || success ? <Check size={14} color="#fff" /> : null}
        </View>
        <View style={{ flex: 1, gap: rpx(2) }}>
          <Typography.Text medium size={rpx(14)} color={aiColors.foreground}>
            {item.label}
          </Typography.Text>
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            {couponBenefitText(item.benefit)}
          </Typography.Text>
        </View>
        <View style={{ alignItems: 'flex-end', gap: rpx(6) }}>
          {success ? (
            <>
              <Typography.Text
                medium
                size={rpx(11)}
                color={aiColors.accentForeground}
              >
                ✓ 已核销 {redeemed} {unit}
              </Typography.Text>
              {redeemedAt ? (
                <Typography.Text
                  size={rpx(11)}
                  color={aiColors.mutedForeground}
                >
                  {dayjs(redeemedAt).format('HH:mm')}
                </Typography.Text>
              ) : null}
              <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
                剩 {left} {unit}
              </Typography.Text>
            </>
          ) : (
            <>
              {usable ? (
                <Pill text={`剩 ${left} ${unit}`} color={aiColors.green} />
              ) : (
                <Pill text="已用完" color={aiColors.mutedForeground} />
              )}
              <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
                已用 {item.redeemedQty}
              </Typography.Text>
              {checked && left > 1 ? (
                <Stepper
                  value={picked}
                  max={limit}
                  disabled={!editable}
                  onChange={onCount}
                />
              ) : null}
            </>
          )}
        </View>
      </Pressable>
    );
  },
);

const BottomBar = memo(
  ({ workspace, onScan }: { workspace: Workspace; onScan?: () => void }) => {
    const { rpx } = useResponsiveContext();
    const { state, actions } = workspace;
    const coupon = state.coupon!;
    const lines = coupon.items.filter(item => state.picks[item.itemId]);
    const count = lines.reduce(
      (sum, item) => sum + (state.picks[item.itemId] ?? 0),
      0,
    );
    const next = () => {
      actions.reset();
      onScan?.();
    };
    switch (state.phase) {
      case ApdmRedemptionPhase.Redeeming:
        return (
          <PrimaryButton
            icon={
              <ActivityIndicator
                size="small"
                color={aiColors.mutedForeground}
              />
            }
            title="核销中…"
            disabled
          />
        );
      case ApdmRedemptionPhase.Unsure:
        return (
          <View style={{ gap: rpx(10) }}>
            <Notice
              tone="error"
              text="核销结果未确认，请重试（不会重复核销）"
            />
            <PrimaryButton
              icon={<RotateCw size={18} color="#fff" />}
              title="重试"
              onPress={() => void actions.redeem()}
            />
            <Pressable
              accessibilityRole="button"
              onPress={actions.abandon}
              style={{ alignSelf: 'center' }}
              hitSlop={8}
            >
              <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
                放弃并重新扫描
              </Typography.Text>
            </Pressable>
          </View>
        );
      case ApdmRedemptionPhase.Done:
      case ApdmRedemptionPhase.Reading:
        return (
          <View style={{ gap: rpx(10) }}>
            {state.error ? <Notice tone="error" text={state.error} /> : null}
            {coupon.items.some(apdmCouponItemUsable) ? (
              <Pressable
                accessibilityRole="button"
                disabled={state.phase === ApdmRedemptionPhase.Reading}
                onPress={() => void actions.continueCoupon()}
                style={{ alignSelf: 'center' }}
                hitSlop={8}
              >
                <Typography.Text size={rpx(13)} color={aiColors.primary}>
                  继续核销这张券
                </Typography.Text>
              </Pressable>
            ) : null}
            <PrimaryButton
              icon={<ScanQrCode size={18} color="#fff" />}
              title="扫描下一张"
              disabled={state.phase === ApdmRedemptionPhase.Reading}
              onPress={next}
            />
          </View>
        );
      default:
        return (
          <View style={{ gap: rpx(10) }}>
            {state.changed ? (
              <Notice tone="info" text="优惠券已变化，已刷新，请重新选择" />
            ) : null}
            {state.error ? <Notice tone="error" text={state.error} /> : null}
            {!coupon.items.some(apdmCouponItemUsable) ? (
              <PrimaryButton
                icon={<ScanQrCode size={18} color="#fff" />}
                title="扫描下一张"
                onPress={next}
              />
            ) : count ? (
              <PrimaryButton
                icon={<CircleCheck size={18} color="#fff" />}
                title={
                  lines.length === 1
                    ? `核销 ${count} 份 — ${lines[0].label}`
                    : `核销 ${count} 份（${lines.length} 项）`
                }
                onPress={() => void actions.redeem()}
              />
            ) : (
              <PrimaryButton title="请选择要核销的项目" disabled />
            )}
          </View>
        );
    }
  },
);

const Fact = memo(({ label, value }: { label: string; value: string }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View style={{ flex: 1, gap: rpx(2) }}>
      <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
        {label}
      </Typography.Text>
      <Typography.Text medium size={rpx(13)} color={aiColors.foreground}>
        {value}
      </Typography.Text>
    </View>
  );
});

const Pill = memo(({ text, color }: { text: string; color: string }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        paddingHorizontal: rpx(8),
        paddingVertical: rpx(2),
        borderRadius: rpx(10),
        backgroundColor: tint(color, 0.12),
      }}
    >
      <Typography.Text medium size={rpx(11)} color={color}>
        {text}
      </Typography.Text>
    </View>
  );
});

const Stepper = memo(
  ({
    value,
    max,
    disabled,
    onChange,
  }: {
    value: number;
    max: number;
    disabled: boolean;
    onChange: (value: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const step = (delta: number, enabled: boolean, icon: ReactNode) => (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={delta > 0 ? '加一份' : '减一份'}
        disabled={disabled || !enabled}
        onPress={() => onChange(value + delta)}
        hitSlop={6}
        style={{
          width: rpx(24),
          height: rpx(24),
          borderRadius: rpx(12),
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor:
            delta > 0 && enabled && !disabled
              ? aiColors.primary
              : aiColors.muted,
        }}
      >
        {icon}
      </Pressable>
    );
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(10),
          paddingHorizontal: rpx(4),
          paddingVertical: rpx(3),
          borderRadius: rpx(16),
          borderWidth: 1,
          borderColor: aiColors.border,
          backgroundColor: aiColors.card,
        }}
      >
        {step(-1, value > 1, <Minus size={14} color={aiColors.foreground} />)}
        <Typography.Text medium size={rpx(13)} color={aiColors.foreground}>
          {value}
        </Typography.Text>
        {step(
          1,
          value < max,
          <Plus
            size={14}
            color={value < max && !disabled ? '#fff' : aiColors.mutedForeground}
          />,
        )}
      </View>
    );
  },
);

const Notice = memo(
  ({ tone, text }: { tone: 'error' | 'info'; text: string }) => {
    const { rpx } = useResponsiveContext();
    const color =
      tone === 'error' ? aiColors.destructive : aiColors.mutedForeground;
    const Icon = tone === 'error' ? TriangleAlert : RefreshCw;
    return (
      <View
        accessibilityRole="alert"
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: rpx(6),
        }}
      >
        <Icon size={14} color={color} />
        <Typography.Text size={rpx(12)} color={color}>
          {text}
        </Typography.Text>
      </View>
    );
  },
);

const PrimaryButton = memo(
  ({
    title,
    icon,
    disabled,
    secondary,
    onPress,
  }: {
    title: string;
    icon?: ReactNode;
    disabled?: boolean;
    /** Outlined, under a primary action. */
    secondary?: boolean;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const foreground = disabled
      ? aiColors.mutedForeground
      : secondary
      ? aiColors.primary
      : '#fff';
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: !!disabled }}
        disabled={disabled}
        onPress={onPress}
        style={({ pressed }) => ({
          alignSelf: 'stretch',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: rpx(8),
          height: rpx(48),
          paddingHorizontal: rpx(16),
          borderRadius: rpx(12),
          borderWidth: secondary && !disabled ? 1 : 0,
          borderColor: aiColors.primary,
          backgroundColor: disabled
            ? aiColors.muted
            : secondary
            ? aiColors.card
            : aiColors.primary,
          opacity: pressed ? 0.85 : 1,
        })}
      >
        {icon}
        <Typography.Text
          medium
          size={rpx(15)}
          color={foreground}
          numberOfLines={1}
        >
          {title}
        </Typography.Text>
      </Pressable>
    );
  },
);
